import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import URL from 'url-parse';
import { LinkType, recognizeLinkTypeByUrl } from 'fym-common/src/utils/recognizeLinkTypeByUrl';
import { SerializedLinkWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { FieldsName, FieldsType } from './consts';
import { FormInput } from '../components/FormInput/FormInput';
import { Spacer } from '../../common/views/spacer/Spacer';
import { resolver } from './validators';
import { SMLFormContainer } from '../../smartLinks/SMLFormContainer/SMLFormContainer';
import { InputType } from '../types';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useGetSmartLinkQuery, useGetSmartLinksQuery } from '../../../redux/api/fymAPI';
import { SMLListChoiceSmartLinkView } from '../../smartLinks/smlListView/SMLListChoiceSmartLinkView';
import { TextLink } from '../../common/views/textLink/TextLink';
import { clearedLinkWidgetFormValues, linkWidgetFormValues, linkWidgetTargetFromSmartLink } from './linkWidgetTarget';

export interface LinkWidgetFormProps {
  widget?: SerializedLinkWidget | null;
  onSubmit: SubmitHandler<FieldsType>;
  onDelete: () => void;
  title: string;
  isLoading?: boolean;
  error?: ErrorAPIResult['error'];
  accountId: number;
  smartLinkId: number;
}

const LinkWidgetFormComponent: React.FC<LinkWidgetFormProps> = ({
  widget,
  onSubmit,
  onDelete,
  title,
  isLoading = false,
  error,
  accountId,
  smartLinkId,
}) => {
  const intl = useIntl();
  const [isChoiceSmartLink, setIsChoiceSmartLink] = useState<boolean>(false);
  // The page this block points at. A saved block already carries the reference,
  // so re-opening it shows the picked page instead of a frozen address.
  const [selectedSmartLinkId, setSelectedSmartLinkId] = useState<number | undefined>(widget?.data.smartLinkId);
  const [isLabelVisible, setIsLabelVisible] = useState<boolean>(false);
  const {
    data: smartLinksResponse,
    isLoading: smartLinksLoading,
    error: smartLinksError,
  } = useGetSmartLinksQuery({ accountId, page: 0, limit: 2 });
  // Whether at least one page other than this one can be linked. The list
  // never contains the bio link, so editing the bio link with a single other
  // page must still offer the picker. With two or more pages listed, at most
  // one of them is this page, so a target always exists; with exactly one,
  // it is a target only when it is not this page.
  const hasMoreSmartLinks = useMemo(() => {
    if (!smartLinksResponse) {
      return false;
    }
    if (smartLinksResponse.totalItems >= 2) {
      return true;
    }
    return smartLinksResponse.totalItems === 1 && smartLinksResponse.results[0]?.id !== smartLinkId;
  }, [smartLinkId, smartLinksResponse]);

  const { smartLink, getSmartLinkError, isGetSmartLinkLoading } = useGetSmartLinkQuery(
    { accountId, smartLinkId: selectedSmartLinkId ?? 0 },
    {
      skip: selectedSmartLinkId === undefined,
      selectFromResult: (result) => ({
        smartLink: result.data?.smartLink,
        isGetSmartLinkLoading: result.isLoading,
        getSmartLinkError: result.error,
      }),
    }
  );
  const target = useMemo(
    () => (smartLink && selectedSmartLinkId !== undefined ? linkWidgetTargetFromSmartLink(smartLink) : null),
    [selectedSmartLinkId, smartLink]
  );
  const { control, handleSubmit, reset, getValues, watch } = useForm<FieldsType>({
    resolver,
    defaultValues: linkWidgetFormValues({ widget: widget?.data }),
  });

  const submit = useCallback(
    (event?: React.BaseSyntheticEvent) => {
      handleSubmit(onSubmit)(event).catch(console.error);
    },
    [handleSubmit, onSubmit]
  );

  const watchedUrlField = watch(FieldsName.url);
  const recognizedUrl = useMemo(() => recognizeLinkTypeByUrl(new URL(watchedUrlField)), [watchedUrlField]);

  useEffect(() => {
    reset(linkWidgetFormValues({ widget: widget?.data, target }));
  }, [reset, target, widget?.data]);

  useEffect(() => {
    if (watchedUrlField !== '' && recognizedUrl === LinkType.generic) {
      setIsLabelVisible(true);
    }
  }, [recognizedUrl, watchedUrlField]);

  const showSMLListView = useCallback(() => {
    setIsChoiceSmartLink(true);
  }, []);

  const onSmartLinkSelected = useCallback((id: number) => {
    setSelectedSmartLinkId(id);
    setIsChoiceSmartLink(false);
  }, []);

  const clearSelectedSmartLink = useCallback(() => {
    setSelectedSmartLinkId(undefined);
    reset(clearedLinkWidgetFormValues(getValues()));
  }, [getValues, reset]);

  const urlHelp = useMemo(() => {
    if (!target) {
      return undefined;
    }
    if (target.isDraft) {
      return intl.formatMessage(
        {
          id: 'app.sml.widgets.link.target.draft',
          defaultMessage: 'Points at your page “{name}”. The page is still a draft, so publish it before you share.',
        },
        { name: target.name }
      );
    }
    return intl.formatMessage(
      {
        id: 'app.sml.widgets.link.target.live',
        defaultMessage: 'Points at your page “{name}”. The address follows the page if you rename it.',
      },
      { name: target.name }
    );
  }, [intl, target]);

  const backRouteParams = useMemo(() => ({ accountId, smartLinkId }), [accountId, smartLinkId]);
  return (
    <SMLFormContainer
      backRoute={SMLRoutes.details}
      backRouteParams={backRouteParams}
      title={title}
      onSubmit={submit}
      submitDisabled={isChoiceSmartLink}
      onDelete={widget ? onDelete : undefined}
      isLoading={isLoading || isGetSmartLinkLoading || smartLinksLoading}
      error={error || getSmartLinkError || smartLinksError}
    >
      {!isChoiceSmartLink ? (
        <>
          <FormInput
            name={FieldsName.url}
            control={control}
            type={InputType.text}
            disabled={isLoading || target !== null}
            label={intl.formatMessage({
              id: 'app.forms.link_widget.url',
              defaultMessage: 'URL',
            })}
            readonly={target !== null}
            help={urlHelp}
          />
          {isLabelVisible ? (
            <FormInput
              name={FieldsName.label}
              control={control}
              type={InputType.text}
              disabled={isLoading}
              label={intl.formatMessage({
                id: 'app.forms.link_widget.label',
                defaultMessage: 'Label',
              })}
            />
          ) : null}
          <Spacer marginBottom={20} />
          {target ? (
            <TextLink onClick={clearSelectedSmartLink}>
              {intl.formatMessage({
                id: 'app.sml.widgets.link.target.clear',
                defaultMessage: 'Link to a web address instead',
              })}
            </TextLink>
          ) : (
            hasMoreSmartLinks && (
              <TextLink onClick={showSMLListView}>
                {intl.formatMessage({
                  id: 'app.sml.widgets.link.target.pick',
                  defaultMessage: 'Link to one of my pages',
                })}
              </TextLink>
            )
          )}
        </>
      ) : (
        <SMLListChoiceSmartLinkView smartLinkId={smartLinkId} onSmartLinkSelected={onSmartLinkSelected} />
      )}
    </SMLFormContainer>
  );
};

export const LinkWidgetForm = TypedMemo(LinkWidgetFormComponent);
